import { Actions, Mutations } from "@/store/enums/StoreEnums";
import { Action, Module, Mutation, VuexModule } from "vuex-module-decorators";
import ApiService from "@/core/services/ApiService";
import { LoginResponse } from "@/store/modules/common/UserAuthModule";
import {
  BankAccount,
  RemotePortalBankAccount,
} from "@/core/services/api/interfaces/manager/BankAccount";
import {
  PortalService,
  PortalServiceEnum,
} from "@/core/services/api/interfaces/dict/PortalService";

@Module
export default class PortalServiceModule extends VuexModule {
  portalRemoteAccounts = {
    [PortalServiceEnum.IBANFLEX]: [] as RemotePortalBankAccount[],
    [PortalServiceEnum.KATBIT]: [] as RemotePortalBankAccount[],
    [PortalServiceEnum.FASTPAY]: [] as RemotePortalBankAccount[],
    [PortalServiceEnum.BREADXPAY]: [] as RemotePortalBankAccount[],
  };

  get portalRemoteAccountsList() {
    return (
      portalService: PortalService | PortalServiceEnum
    ): RemotePortalBankAccount[] => {
      const slug =
        typeof portalService === "string" ? portalService : portalService.value;

      return this.portalRemoteAccounts[slug] || [];
    };
  }

  get portalRemoteAccountFirst() {
    return (
      portalService: PortalService | PortalServiceEnum
    ): null | RemotePortalBankAccount => {
      const slug =
        typeof portalService === "string" ? portalService : portalService.value;
      const accounts = this.portalRemoteAccountsList(slug);
      if (accounts && accounts.length) {
        return accounts[0];
      }

      return null;
    };
  }

  get portalRemoteAccountByID() {
    return (
      portalService: PortalService | PortalServiceEnum,
      id: string
    ): null | RemotePortalBankAccount => {
      const slug =
        typeof portalService === "string" ? portalService : portalService.value;
      const accounts = this.portalRemoteAccountsList(slug);
      if (!accounts || !accounts.length) {
        return null;
      }
      for (const account of accounts) {
        if (account.id === id) {
          return account;
        }
      }

      return null;
    };
  }

  @Action({ rawError: true })
  async [Actions.MANAGER_PORTAL_SERVICES_SIGN_IN](portalService) {
    return ApiService.manager.portal_service
      .signInPortalService(portalService)
      .then(ApiService.unwrapItemResponse<{ url: string }>)
      .then(({ url }) => window.open(url, "_blank"));
  }

  @Action({ rawError: true })
  async [Actions.MANAGER_PORTAL_SERVICES_SIGN_UP_COMPANY]({
    portalService,
    questionnaire,
  }) {
    return ApiService.manager.portal_service
      .signUpCompany(portalService, questionnaire)
      .then(ApiService.unwrapItemResponse<LoginResponse>)
      .then((response: LoginResponse) => {
        return Promise.all([
          this.context.dispatch(Actions.MANAGER_COMPANY_ACCOUNTS_LOAD),
          this.context.commit(Mutations.COMMON_AUTH_SET, response.token),
          this.context.commit(Mutations.COMMON_USER_SET, response.user),
        ]);
      });
  }

  @Action({ rawError: true })
  async [Actions.MANAGER_PORTAL_SERVICES_SIGN_UP_EMPLOYEE]({
    portalService,
    questionnaire,
  }) {
    return ApiService.manager.portal_service
      .signUpEmployee(portalService, questionnaire)
      .then(ApiService.unwrapItemResponse<LoginResponse>)
      .then((response: LoginResponse) => {
        return Promise.all([
          this.context.dispatch(Actions.MANAGER_COMPANY_ACCOUNTS_LOAD),
          this.context.commit(Mutations.COMMON_AUTH_SET, response.token),
          this.context.commit(Mutations.COMMON_USER_SET, response.user),
        ]);
      });
  }

  @Action({ rawError: true })
  async [Actions.MANAGER_PORTAL_SERVICES_AUTO_TRANSFER_CONFIGURE](request) {
    return ApiService.manager.portal_service.configureAutoTransfer(request);
  }

  @Action({ rawError: true })
  async [Actions.MANAGER_PORTAL_SERVICES_AUTO_TRANSFER_REMOVE](
    account: BankAccount | number
  ) {
    const account_id = typeof account === "number" ? account : account.id;

    return ApiService.manager.portal_service.removeAutoTransfer(account_id);
  }

  @Action({ rawError: true })
  async [Actions.MANAGER_PORTAL_SERVICES_LOAD_REMOTE_ACCOUNTS](portalService) {
    return ApiService.manager.portal_service
      .getRemoteAccounts(portalService)
      .then(ApiService.unwrapItemResponse)
      .then((response) => {
        this.context.commit(
          Mutations.MANAGER_PORTAL_SERVICE_SET_REMOTE_ACCOUNTS,
          { portalService, accounts: response }
        );
      })
      .catch(() => {
        this.context.commit(
          Mutations.MANAGER_PORTAL_SERVICE_SET_REMOTE_ACCOUNTS,
          { portalService, accounts: [] }
        );
      });
  }

  @Mutation
  [Mutations.MANAGER_PORTAL_SERVICE_SET_REMOTE_ACCOUNTS]({
    portalService,
    accounts,
  }) {
    const slug =
      typeof portalService === "string" ? portalService : portalService.value;
    this.portalRemoteAccounts[slug] = accounts;

    console.log(this.portalRemoteAccounts[slug]);
  }
}
